// small helper module tracking focus and distinguishing real user input
// from browser autofill behavior using Effector stores/events
import type { Event } from 'effector'

import { createEffect, createEvent, createStore, sample } from 'effector'
import { aye } from '~/shared/helpers'

// event emitted whenever the document focus state changes
const focusedElementChanged = createEvent<Element | null>()

// store holding the most recently focused DOM element
const $focusedElement = createStore<Element | null>(null).on(
  focusedElementChanged,
  (_, element) => element
)

// Chrome sometimes autofills inputs without any focus or explicit user interaction.
// When a form field update event fires, we only want to consider it a "user input"
// if some element was focused at that moment. helpers.aye checks for truthiness.
export const actualUserInput = <T>(fieldsUpdateEvent: Event<T>) => {
  // derived event that will fire only for genuine user-driven updates
  const userUpdatesForm = createEvent<T>()

  // sample the focused element when updates occur; forward the payload
  // only if there is an active element (user was interacting)
  sample({
    clock: fieldsUpdateEvent,
    source: $focusedElement,
    filter: (focusedElement) => aye(focusedElement),
    fn: (_, clock) => clock,
    target: userUpdatesForm,
  })

  return userUpdatesForm
}

const setupFocusTrackingFx = createEffect(() => {
  const handler = () => {
    focusedElementChanged(document.activeElement)
  }

  document.addEventListener('focusin', handler)
  document.addEventListener('focusout', handler)
})

// initialize focus tracking immediately
setupFocusTrackingFx()
